import React from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { AIInsightsPanel } from "@/components/analytics/ai-insights-panel";
import { getFormattedDateTime } from "@/utils";

// Sample member data - in a real app this would come from an API
const membersData = [
  {
    id: "1",
    firstName: "Alex",
    lastName: "Johnson",
    email: "alex@example.com",
    mobile: "+1 123-456-7890",
    city: "New York",
    country: "United States",
    programId: "1",
    programName: "Premium Rewards",
    status: "Active",
    joinDate: "2023-05-15",
    pointsBalance: 2500,
    lastActivity: "2023-11-10",
  },
  {
    id: "2",
    firstName: "Sam",
    lastName: "Smith",
    email: "sam@example.com",
    mobile: "+1 234-567-8901",
    city: "Los Angeles",
    country: "United States",
    programId: "2",
    programName: "Fitness Points",
    status: "Active",
    joinDate: "2023-06-22",
    pointsBalance: 1800,
    lastActivity: "2023-10-05",
  },
  // More members...
];

// Sample transactions for the member
const transactionData = [
  {
    id: "TR001",
    date: "2023-11-10",
    type: "Earn",
    points: 250,
    description: "Purchase at Coffee Shop",
  },
  {
    id: "TR002",
    date: "2023-10-25",
    type: "Redeem",
    points: -100,
    description: "Free Drink Redemption",
  },
  {
    id: "TR003",
    date: "2023-10-15",
    type: "Earn",
    points: 150,
    description: "Purchase at Coffee Shop",
  },
];

export default function MemberDetails() {
  const { id } = useParams<{ id: string }>();
  const member = membersData.find(m => m.id === id);

  if (!member) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold mb-4">Member Not Found</h2>
        <p className="text-muted-foreground mb-6">The member you're looking for doesn't exist or has been removed.</p>
        <Button asChild>
          <Link to="/perkpoint/members">Back to Members</Link>
        </Button>
      </div>
    );
  }

  const memberSampleQuestions = [
    "What are this member's engagement patterns?",
    "How does this member compare to others?",
    "When is this member most active?",
    "What's the redemption behavior?",
    "Show me spending trends",
    "Predict member lifetime value"
  ];

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to="/perkpoint/members">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{member.firstName} {member.lastName}</h1>
            <p className="text-gray-500">Member profile and activity</p>
          </div>
        </div>
        <Button className="bg-green-600 hover:bg-green-700" asChild>
          <Link to={`/perkpoint/edit-member/${member.id}`}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Member
          </Link>
        </Button>
      </div>

      {/* Status badge and program info */}
      <div className="flex items-center gap-4 flex-wrap">
        <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">
          {member.status}
        </Badge>
        <span className="text-sm text-muted-foreground">
          Program: <span className="font-medium text-gray-800">{member.programName}</span>
        </span>
        <span className="text-sm text-muted-foreground">
          Joined: {getFormattedDateTime(member.joinDate)}
        </span>
        <span className="text-sm text-muted-foreground">
          Last Activity: {getFormattedDateTime(member.lastActivity)}
        </span>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-4 h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="rewards">Rewards</TabsTrigger>
          <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Member details */}
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle>Member Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">First Name</h3>
                  <p className="text-base">{member.firstName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Last Name</h3>
                  <p className="text-base">{member.lastName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Email</h3>
                  <p className="text-base">{member.email}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Mobile</h3>
                  <p className="text-base">{member.mobile}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">City</h3>
                  <p className="text-base">{member.city}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Country</h3>
                  <p className="text-base">{member.country}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Points summary */}
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle>Points Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Current Balance</h3>
                  <p className="text-2xl font-semibold text-green-600">{member.pointsBalance} points</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Earned</h3>
                  <p className="text-2xl font-semibold">{member.pointsBalance + 500} points</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Redeemed</h3>
                  <p className="text-2xl font-semibold">500 points</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="activity" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>
                Recent points transactions for this member
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                title="Transactions"
                data={transactionData}
                columns={[
                  {
                    header: "ID",
                    accessorKey: "id",
                  },
                  {
                    header: "Date",
                    accessorKey: "date",
                    cell: (row) => getFormattedDateTime(row.date) || row.date,
                  },
                  {
                    header: "Type",
                    accessorKey: "type",
                    cell: (row) => (
                      <Badge 
                        variant="outline" 
                        className={row.type === "Earn" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}
                      >
                        {row.type}
                      </Badge>
                    ),
                  },
                  {
                    header: "Points",
                    accessorKey: "points",
                    cell: (row) => (
                      <span className={row.points > 0 ? "text-green-600" : "text-blue-600"}>
                        {row.points > 0 ? `+${row.points}` : row.points}
                      </span>
                    ),
                  },
                  {
                    header: "Description",
                    accessorKey: "description",
                  },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="rewards" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Available Rewards</CardTitle>
              <CardDescription>Rewards this member can redeem</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Rewards functionality will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-insights" className="mt-6">
          <AIInsightsPanel
            title="Member AI Analytics"
            dataType="members"
            sampleQuestions={memberSampleQuestions}
            data={[member]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
